import React, { Fragment, useEffect, useState } from 'react';
import log from 'chameleon/ui-stack/utilities/log';
import dayjs from 'dayjs';
import WarningModal from 'chameleon/ui-stack/ui/modals/warning-modal';
import { Link, withRouter } from 'react-router';
import trackerLog from 'legacy/packages/wizards/src/utils/trackerLog';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { clearDataFromLocalStorage } from '../LocalStorage';
import {
  Actions,
  getBlockedDomains,
  UserRoles,
  getIconByAction,
} from './domains';
import './FirewallCheck.scss';
import {
  DebugSecureFirewallCheck,
  removeDebugKeysFromLocalStorage,
} from './browser-debug';
import { Grid } from '@material-ui/core';
import { isLockdownBrowser } from '../../components/auth/utils';

const { areDebugKeysSetInLocalStorage } = DebugSecureFirewallCheck;
const INTERVAL_CHECK_TEN_MINUTES_AS_MILLIS = 10 * 60 * 1000;
export const FIREWALL_CHECK_KEY = 'secure-firewall-check';

const SecureFirewallCheck = ({
  userData,
  router,
  featureEnabled,
  nextCheckHours,
  intervalMillis = INTERVAL_CHECK_TEN_MINUTES_AS_MILLIS,
}) => {
  const [showModal, setShowModal] = useState(false);
  const initialDomainsState = { warnDomains: [], blockDomains: [] };
  const [domains, setDomains] = useState(initialDomainsState);

  const isUserLoggedIn = () =>
    userData.isTeacher ||
    userData.isStudent ||
    userData.isCoordinator ||
    userData.isAdmin;

  const handleConfirm = (hasBlockedDomains) => {
    setShowModal(false);
    updateUserAcknowledgement();
    if (hasBlockedDomains) {
      window.location.href = ROS_FRONTEND_URL;
    }
  };

  const trackDomains = (userInformation, warn, block) => {
    const userId = userInformation.me.initId;
    const userRoles = userInformation.me.userRole;
    const groupIds = userInformation.me.schoolSet.map(
      (school) => school.initId,
    );
    let data = { userId, userRoles, groupIds, warn, block };

    trackerLog('secure_firewallcheck', 'secure_firewallcheck', '/', data);
  };

  const intervalCheck = () => {
    if (isCheckNeeded()) {
      return findBlockedDomains(userData).then(({ warn, block }) => {
        if (shouldShowModal(warn, block, userHasJustLoggedIn)) {
          setShowModal(true);
          setDomains({ warnDomains: warn, blockDomains: block });
          trackDomains(userData, warn, block);
        }
        updateNextCheck(
          block.length > 0,
          warn.length > 0,
          false,
          dayjs(),
          nextCheckHours,
        );
        return Promise.resolve('performed secure firewall check');
      });
    }
    return Promise.resolve('secure firewall check not needed');
  };

  useEffect(() => {
    if (!isUserLoggedIn() || isLockdownBrowser()) return;
    if (router.location.pathname === '/logout') {
      log(
        `user logged out, removing localStorage item "${FIREWALL_CHECK_KEY}"`,
      );
      clearDataFromLocalStorage(FIREWALL_CHECK_KEY);
      removeDebugKeysFromLocalStorage();
      return;
    }
    intervalCheck().then((r) => log(r));
    const intervalId = setInterval(intervalCheck, intervalMillis);
    return () => {
      clearInterval(intervalId);
      setShowModal(false);
      setDomains(initialDomainsState);
    };
  }, []);

  useEffect(() => {
    if (!featureEnabled) {
      log(`Secure Firewall Check is not enabled`);
    }
  }, [featureEnabled]);

  if (!featureEnabled) {
    return <div />;
  }

  if (showModal) {
    let hasBlockedDomains = domains.blockDomains.length > 0;
    const confirmLabel = hasBlockedDomains
      ? `RETURN TO AP ${userData.isStudent ? 'STUDENTS' : 'CENTRAL'}`
      : `CONTINUE TO AP CLASSROOM`;

    return (
      <WarningModal
        title="Warning"
        onSuccess={() => handleConfirm(hasBlockedDomains)}
        content={{
          btnPrimary: confirmLabel,
        }}
      >
        <WarningContent
          warnDomains={domains.warnDomains}
          blockDomains={domains.blockDomains}
        />
      </WarningModal>
    );
  }

  // https://reactjs.org/docs/jsx-in-depth.html#booleans-null-and-undefined-are-ignored
  return <div />;
};

const WarningContent = ({ warnDomains, blockDomains }) => (
  <Fragment>
    <p>
      AP Classroom leverages services that your web browser is currently
      blocking. By safe-listing the following site domain(s), you’ll be able to
      access all AP resources. If you are using a school device or network, you
      may have to contact your network administrator for assistance.
    </p>
    <div className="SecureFirewallCheck__listDomains">
      {warnDomains.map((d) => (
        <p key={d}>
          {getIconByAction(Actions.WARN)} {d}
        </p>
      ))}
      {blockDomains.map((d) => (
        <p key={d}>
          {getIconByAction(Actions.BLOCK)} {d}
        </p>
      ))}
    </div>
    <div>
      <p className={'SecureFirewallCheck__about-results'}>About your results</p>
      {warnDomains.length > 0 && blockDomains.length > 0 && (
        <WarningAndBlockedMessage />
      )}
      {warnDomains.length > 0 && blockDomains.length === 0 && (
        <WarningOnlyMessage />
      )}
      {warnDomains.length === 0 && blockDomains.length > 0 && (
        <BlockedOnlyMessage />
      )}
    </div>
  </Fragment>
);

const WarningOnlyMessage = () => (
  <p>
    <b>
      If you do not safe-list or unblock these site(s), some features will not
      work or be available.{' '}
    </b>
    To get additional information on the functionality provided by these sites,
    visit the <Link to="/readinesscheck">AP Classroom readiness check</Link>.
  </p>
);

const BlockedOnlyMessage = () => (
  <p>
    <b>
      If you do not safe-list or unblock these site(s), you will not be able to
      enter AP Classroom.{' '}
    </b>
    To get additional information on the functionality provided by these sites,
    visit the <Link to="/readinesscheck">AP Classroom readiness check</Link>.
  </p>
);

const WarningAndBlockedMessage = () => (
  <React.Fragment>
    <Grid container>
      <Grid item xs={1}>
        {getIconByAction(Actions.WARN)}
      </Grid>
      <Grid item xs={11}>
        <p>
          If you do not safe-list or unblock this site, use AP Classroom with
          caution: some features will not work as intended or be available.
        </p>
      </Grid>
    </Grid>

    <Grid container>
      <Grid item xs={1}>
        {getIconByAction(Actions.BLOCK)}
      </Grid>
      <Grid item xs={11}>
        <p>
          If you do not safe-list or unblock this site, you will not be able to
          enter AP Classroom.
        </p>
      </Grid>
    </Grid>
    <br />
    <p>
      To get additional information on the functionality provided by these
      sites, visit the{' '}
      <Link to="/readinesscheck">AP Classroom readiness check</Link>.
    </p>
  </React.Fragment>
);

export const getRoleAndPrograms = (data) => {
  if (data.isStudent) {
    return {
      role: UserRoles.STUDENT,
      programs: new Set(data.studentSubjects.map((s) => s.subjectProgram)),
    };
  } else if (data.isTeacher) {
    return {
      role: UserRoles.TEACHER,
      programs: new Set(data.teacherSubjects.map((s) => s.subjectProgram)),
    };
  } else if (data.isAdmin) {
    return {
      role: UserRoles.ADMIN,
      programs: new Set(data.adminSubjects.map((s) => s.subjectProgram)),
    };
  } else if (data.isCoordinator) {
    // coordinator shares the same subjects as admin
    return {
      role: UserRoles.COORDINATOR,
      programs: new Set(data.adminSubjects.map((s) => s.subjectProgram)),
    };
  }

  log(`user has no role and programs in ${JSON.stringify(data)}`);
  return { role: '', programs: new Set() };
};

const sessionData = JSON.parse(sessionStorage.getItem('apc_user'));

const userHasJustLoggedIn =
  sessionData &&
  (sessionData._lastPageUrl === `${FYM_FRONTEND_URL}/subjects/preview` ||
    sessionData._lastPageUrl === FYM_FRONTEND_URL);

export const shouldShowModal = (
  warnDomains,
  blockDomains,
  userHasJustLoggedIn,
) => {
  let shouldShowModal = false;
  if (
    userHasJustLoggedIn &&
    (warnDomains.length > 0 || blockDomains.length > 0)
  ) {
    shouldShowModal = true;
  }
  return shouldShowModal;
};

export const isCheckNeeded = (from = dayjs()) => {
  if (areDebugKeysSetInLocalStorage()) return true;

  let stored = JSON.parse(localStorage.getItem(FIREWALL_CHECK_KEY));
  if (stored === null) return true;

  return (
    stored.hasBlockingDomains ||
    (stored.hasWarningDomains && !stored.userConfirmedBlockedDomains) ||
    from.isAfter(dayjs(stored.nextCheckAfter))
  );
};

export const updateNextCheck = (
  hasBlockingDomains,
  hasWarningDomains,
  userConfirmedBlockedDomains = false,
  from = dayjs(),
  numHours = 24,
) => {
  const nextCheckAfter = from.add(numHours, 'h');
  const localStore = {
    hasBlockingDomains,
    hasWarningDomains,
    userConfirmedBlockedDomains,
    nextCheckAfter: nextCheckAfter.toString(),
  };
  log(`secure firewall status ${JSON.stringify(localStore)}`);
  localStorage.setItem(FIREWALL_CHECK_KEY, JSON.stringify(localStore));
  return nextCheckAfter;
};

const updateUserAcknowledgement = () => {
  let stored = JSON.parse(localStorage.getItem(FIREWALL_CHECK_KEY));
  if (stored.hasWarningDomains) {
    let updated = Object.assign({}, stored, {
      userConfirmedBlockedDomains: true,
    });
    localStorage.setItem(FIREWALL_CHECK_KEY, JSON.stringify(updated));
  }
};

export const findBlockedDomains = (userData) => {
  const { role, programs } = getRoleAndPrograms(userData);
  return getBlockedDomains(role, programs);
};

export default compose(
  withRouter,
  connect((state) => ({
    featureEnabled: state.featureFlag.secure_firewall_check_enable,
    nextCheckHours:
      state.featureFlag.secure_firewall_check_interval_duration_hours,
  })),
)(SecureFirewallCheck);
